<script setup>
// Szukanie produktu w katalogu przy dodawaniu składnika. Zwykły `datalist` nie
// pokazywał, ile produkt ma kalorii, i wymagał trafienia w nazwę co do znaku —
// tutaj lista filtruje się na bieżąco, a ulubione są na górze.
// `allowFree` pozwala dopisać nazwę spoza katalogu (wpis dnia to dopuszcza,
// skład zapisanego posiłku nie — tam składnik musi mieć swój produkt).
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { looseIncludes, norm } from '@/composables/useV2Text.js';
import { perPortion } from '@/composables/useSemiProducts.js';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
    modelValue: { type: String, default: '' },
    product: { type: Object, default: null },
    products: { type: Array, default: () => [] },
    allowFree: { type: Boolean, default: false },
    id: { type: String, default: 'v2-product-picker' },
    label: { type: String, default: null },
    // brak = domyślna podpowiedź z tłumaczeń
    placeholder: { type: String, default: null },
    disabled: { type: Boolean, default: false },
});
// `update:product` mówi tylko, że tekst pasuje do produktu z katalogu; `pick`
// znaczy, że użytkownik świadomie wybrał pozycję z listy. Rozróżnienie jest
// istotne tam, gdzie wybór od razu coś dodaje.
const emit = defineEmits(['update:modelValue', 'update:product', 'pick', 'submit']);

const LIMIT = 8;

const open = ref(false);
const cursor = ref(0);
const root = ref(null);
const input = ref(null);

const matches = computed(() => {
    const term = props.modelValue.trim();
    const pool = term
        ? props.products.filter(p => looseIncludes(p.name, term))
        : props.products;
    return [...pool]
        // Ulubione wyżej — to po nie sięga się najczęściej.
        .sort((a, b) => Number(!!b.is_favorite) - Number(!!a.is_favorite)
            || (a.name ?? '').localeCompare(b.name ?? '', 'pl'))
        .slice(0, LIMIT);
});

const exact = computed(() => {
    const term = norm(props.modelValue);
    return props.products.find(p => norm(p.name) === term) ?? null;
});

const freeName = computed(() => props.allowFree && props.modelValue.trim() && !exact.value);

const setText = (value) => {
    emit('update:modelValue', value);
    // Ręczna zmiana tekstu unieważnia wybór z listy — dopóki znów nie trafi w nazwę.
    const found = props.products.find(p => norm(p.name) === norm(value));
    emit('update:product', found ?? null);
    open.value = true;
    cursor.value = 0;
};

const pick = (p) => {
    emit('update:modelValue', p.name);
    emit('update:product', p);
    emit('pick', p);
    open.value = false;
};

const move = (delta) => {
    const count = matches.value.length;
    if (!count) return;
    open.value = true;
    cursor.value = (cursor.value + delta + count) % count;
};

// Escape najpierw zamyka listę podpowiedzi; dopiero drugi zamyka modal, w którym
// pole siedzi (bez zatrzymania zdarzenia zamykałyby się oba naraz).
const onEsc = (event) => {
    if (!open.value) return;
    event.preventDefault();
    event.stopPropagation();
    open.value = false;
};

const onEnter = () => {
    if (open.value && matches.value[cursor.value] && !freeName.value) {
        pick(matches.value[cursor.value]);
        return;
    }
    open.value = false;
    emit('submit');
};

watch(() => props.modelValue, (value) => { if (!value) cursor.value = 0; });

const onPointerDown = (event) => {
    if (open.value && !root.value?.contains(event.target)) open.value = false;
};

onMounted(() => document.addEventListener('mousedown', onPointerDown));
onUnmounted(() => document.removeEventListener('mousedown', onPointerDown));

// Rodzic czyści pole po dodaniu składnika — wtedy wracamy kursorem do wpisywania.
const focus = async () => { await nextTick(); input.value?.focus(); };
defineExpose({ focus });
</script>

<template>
    <div ref="root" class="relative">
        <!-- Etykietę rysuje rodzic, gdy sam już ją ma (np. dialog rekordu) -->
        <label v-if="label" class="v2-label" :for="id">{{ label }}</label>
        <input
            :id="id"
            ref="input"
            :value="modelValue"
            type="text"
            role="combobox"
            :aria-expanded="open"
            aria-autocomplete="list"
            class="v2-input"
            :placeholder="placeholder ?? t('food.picker.placeholder')"
            :disabled="disabled"
            @input="setText($event.target.value)"
            @focus="open = true"
            @keydown.down.prevent="move(1)"
            @keydown.up.prevent="move(-1)"
            @keydown.enter.prevent="onEnter"
            @keydown.esc="onEsc"
        />

        <div
            v-if="open && (matches.length || modelValue.trim())"
            class="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-[#E4DDD1] rounded-xl shadow-[0_12px_28px_-14px_rgba(33,30,26,0.35)] overflow-hidden"
        >
            <ul v-if="matches.length" role="listbox" class="max-h-[220px] overflow-y-auto py-1">
                <li v-for="(p, i) in matches" :key="(p.semi ? 'semi-' : '') + p.uuid">
                    <button
                        type="button"
                        role="option"
                        :aria-selected="product?.uuid === p.uuid"
                        class="w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors"
                        :class="i === cursor ? 'bg-[#F2ECE1]' : 'hover:bg-[#FBF9F4]'"
                        @mouseenter="cursor = i"
                        @click="pick(p)"
                    >
                        <i
                            class="pi pi-star-fill text-[8px] w-2.5 shrink-0"
                            :class="p.is_favorite ? 'text-[#C0764B]' : 'text-transparent'"
                            aria-hidden="true"
                        ></i>
                        <span class="flex-1 min-w-0 truncate text-[13px] text-[#211E1A]">{{ p.name }}</span>
                        <!-- Półprodukt liczy się w porcjach, więc i kalorie podajemy na porcję -->
                        <span v-if="p.semi" class="shrink-0 text-[11.5px] tabular-nums text-[#A29A8E]">
                            {{ t('food.picker.semi', { kcal: Math.round(perPortion(p.semi, 'calories')), portion: p.portion_name }) }}
                        </span>
                        <span v-else class="shrink-0 text-[11.5px] tabular-nums text-[#A29A8E]">
                            {{ p.calories ?? 0 }} kcal / 100 g
                        </span>
                    </button>
                </li>
            </ul>

            <p v-if="freeName" class="px-3 py-2 text-[11.5px] text-[#8A8378] border-t border-[#EFE9DF]">
                {{ t('food.picker.free', { name: modelValue.trim() }) }}
            </p>
            <p v-else-if="!matches.length" class="px-3 py-2.5 text-[12.5px] text-[#8A8378]">
                {{ t('food.picker.none') }}
            </p>
        </div>
    </div>
</template>
